import { normalizeUnit, roundMeters, roundQuantity } from "../utils";
import type { BOQExtractionPosition } from "../../utils/types";
import type {
  BuildCountListRows,
  CountListDetectedType,
  CountListDetectedTypeKind,
  CountListPlanColumn,
  CountListRow,
  CountListUnmatchedSource,
} from "./types";

interface UnmatchedAccumulator {
  source: CountListUnmatchedSource;
  name: string;
  typeIdByFloorPlanId: Record<number, number>;
  unit: string;
  planValues: Record<number, number>;
  diagramsValue: number;
}

interface AddDetectedTypeArgs {
  lvMatchId: string;
  kind: CountListDetectedTypeKind;
  name: string;
  typeId: number;
  floorPlanId: number;
}

type AddDetectedType = (args: AddDetectedTypeArgs) => void;

interface ResolveSectionNumberArgs {
  position: BOQExtractionPosition;
  sectionSegmentCount: number;
}

type ResolveSectionNumber = (args: ResolveSectionNumberArgs) => string;

// The extraction is unvalidated S3 JSON, so the number is not guaranteed to be there
const splitPositionNumber = (positionNumber: string): string[] =>
  (positionNumber || "").replace(/[()]/g, "").trim().split(".").filter(Boolean);

// Positions are numbered at one depth throughout a document; anything else is a sub-position of that level
const resolveSectionSegmentCount = (
  positions: BOQExtractionPosition[],
): number => {
  const countByDepth = new Map<number, number>();
  for (const position of positions) {
    const depth = splitPositionNumber(position.position_number).length;
    if (depth < 2) continue;
    countByDepth.set(depth, (countByDepth.get(depth) ?? 0) + 1);
  }

  let dominantDepth = 0;
  let dominantCount = 0;
  for (const [depth, count] of countByDepth) {
    if (count <= dominantCount) continue;
    dominantDepth = depth;
    dominantCount = count;
  }
  return dominantDepth === 0 ? 0 : dominantDepth - 1;
};

// The extraction garbles section_number (dates, truncated levels), but the position number always carries the hierarchy
const resolveSectionNumber: ResolveSectionNumber = ({
  position,
  sectionSegmentCount,
}) => {
  const segments = splitPositionNumber(position.position_number);
  if (segments.length < 2) return position.section_number || "";
  // A sub-position (one level deeper) still belongs to its parent's section
  if (sectionSegmentCount > 0 && segments.length > sectionSegmentCount) {
    return segments.slice(0, sectionSegmentCount).join(".");
  }
  return segments.slice(0, -1).join(".");
};

const addToCountMap = (
  countMap: Map<string, number>,
  lvMatchId: string,
  value: number,
) => {
  countMap.set(lvMatchId, (countMap.get(lvMatchId) ?? 0) + value);
};

export const buildCountListRows: BuildCountListRows = ({
  boqExtraction,
  perPlanData,
  unitLabels,
}) => {
  const planColumns: CountListPlanColumn[] = perPlanData.plans.map(
    ({ floorPlanId, planName, folderId, folderName }) => ({
      floorPlanId,
      planName,
      folderId,
      folderName,
    }),
  );

  const countsByPlan = new Map<number, Map<string, number>>();
  const diagramsCounts = new Map<string, number>();
  const unmatchedByKey = new Map<string, UnmatchedAccumulator>();
  // Groups keep their identity after matching an LV position, so the sheet can show and rename what was detected
  const detectedTypesByLvMatchId = new Map<
    string,
    Map<string, CountListDetectedType>
  >();

  const addDetectedType: AddDetectedType = ({
    lvMatchId,
    kind,
    name,
    typeId,
    floorPlanId,
  }) => {
    const byKey =
      detectedTypesByLvMatchId.get(lvMatchId) ??
      new Map<string, CountListDetectedType>();
    detectedTypesByLvMatchId.set(lvMatchId, byKey);

    const key = `${kind}:${name}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.typeIdByFloorPlanId[floorPlanId] = typeId;
      return;
    }
    byKey.set(key, {
      kind,
      name,
      typeIdByFloorPlanId: { [floorPlanId]: typeId },
    });
  };

  const knownPositionNumbers = new Set(
    (boqExtraction?.positions ?? []).map(
      (position) => position.position_number,
    ),
  );
  // A match pointing at a position missing from the LV would otherwise be silently dropped
  const isMatchedToLv = (lvMatchId?: string): lvMatchId is string =>
    !!lvMatchId && knownPositionNumbers.has(lvMatchId);

  const resolveUnmatched = (
    source: CountListUnmatchedSource,
    name: string,
    unit: string,
  ) => {
    const key = `${source}:${name}`;
    const existing = unmatchedByKey.get(key);
    if (existing) return existing;
    const created: UnmatchedAccumulator = {
      source,
      name,
      typeIdByFloorPlanId: {},
      unit,
      planValues: {},
      diagramsValue: 0,
    };
    unmatchedByKey.set(key, created);
    return created;
  };

  for (const plan of perPlanData.plans) {
    const planCounts = new Map<string, number>();
    countsByPlan.set(plan.floorPlanId, planCounts);

    for (const group of plan.groups) {
      if (isMatchedToLv(group.lvMatchId)) {
        addToCountMap(planCounts, group.lvMatchId, group.count);
        addDetectedType({
          lvMatchId: group.lvMatchId,
          kind: "element",
          name: group.groupId,
          typeId: group.elementTypeId,
          floorPlanId: plan.floorPlanId,
        });
        continue;
      }
      const unmatchedRow = resolveUnmatched(
        "element",
        group.groupId,
        unitLabels.piece,
      );
      unmatchedRow.typeIdByFloorPlanId[plan.floorPlanId] = group.elementTypeId;
      unmatchedRow.planValues[plan.floorPlanId] =
        (unmatchedRow.planValues[plan.floorPlanId] ?? 0) + group.count;
    }

    for (const routeGroup of plan.routeGroups) {
      // Round before summing so totals match the visible per-plan cells exactly
      const meters = roundMeters(routeGroup.meters);
      if (isMatchedToLv(routeGroup.lvMatchId)) {
        addToCountMap(planCounts, routeGroup.lvMatchId, meters);
        addDetectedType({
          lvMatchId: routeGroup.lvMatchId,
          kind: "route",
          name: routeGroup.label,
          typeId: routeGroup.routeTypeId,
          floorPlanId: plan.floorPlanId,
        });
        continue;
      }
      const unmatchedRow = resolveUnmatched(
        "route",
        routeGroup.label,
        unitLabels.meters,
      );
      unmatchedRow.typeIdByFloorPlanId[plan.floorPlanId] =
        routeGroup.routeTypeId;
      unmatchedRow.planValues[plan.floorPlanId] =
        (unmatchedRow.planValues[plan.floorPlanId] ?? 0) + meters;
    }
  }

  for (const diagram of perPlanData.diagrams) {
    for (const itemType of diagram.itemTypes) {
      const count = roundQuantity(itemType.totalCount);
      if (isMatchedToLv(itemType.lvMatchId)) {
        addToCountMap(diagramsCounts, itemType.lvMatchId, count);
        continue;
      }
      const name = itemType.text
        ? `${itemType.item} — ${itemType.text}`
        : itemType.item;
      const unmatchedRow = resolveUnmatched(
        "diagram",
        name,
        normalizeUnit(itemType.unit, unitLabels),
      );
      unmatchedRow.diagramsValue = roundQuantity(
        unmatchedRow.diagramsValue + count,
      );
    }
  }

  const rows: CountListRow[] = [];

  if (boqExtraction) {
    const sectionSegmentCount = resolveSectionSegmentCount(
      boqExtraction.positions,
    );
    let lastSectionKey: string | null = null;

    for (const position of boqExtraction.positions) {
      const sectionNumber = resolveSectionNumber({
        position,
        sectionSegmentCount,
      });
      const sectionKey = `${sectionNumber}|${position.section_name}`;

      if (sectionKey !== lastSectionKey) {
        rows.push({
          kind: "section",
          sectionNumber,
          sectionName: position.section_name,
        });
        lastSectionKey = sectionKey;
      }

      const planValues: Record<number, number> = {};
      let countedTotal = 0;
      for (const planColumn of planColumns) {
        const value =
          countsByPlan
            .get(planColumn.floorPlanId)
            ?.get(position.position_number) ?? 0;
        if (value === 0) continue;
        planValues[planColumn.floorPlanId] = value;
        countedTotal += value;
      }
      const diagramsValue = roundQuantity(
        diagramsCounts.get(position.position_number) ?? 0,
      );
      const detectedTypes = detectedTypesByLvMatchId.get(
        position.position_number,
      );

      rows.push({
        kind: "position",
        positionNumber: position.position_number,
        description: position.description,
        detectedTypes: detectedTypes ? [...detectedTypes.values()] : [],
        lvQuantity: position.quantity,
        unit: position.unit,
        // Diagram counts may re-describe items already counted on plans, so they stay informational
        countedTotal: roundQuantity(countedTotal),
        planValues,
        diagramsValue,
      });
    }
  }

  for (const unmatched of unmatchedByKey.values()) {
    const planValues: Record<number, number> = {};
    let planTotal = 0;
    for (const [floorPlanId, value] of Object.entries(unmatched.planValues)) {
      if (value === 0) continue;
      planValues[Number(floorPlanId)] = value;
      planTotal += value;
    }
    // Diagram counts may re-describe items already counted on plans, so they stay informational
    const countedTotal = roundQuantity(planTotal);
    // A group that exists but was never counted adds no information here
    if (countedTotal === 0 && unmatched.diagramsValue === 0) continue;
    rows.push({
      kind: "unmatched",
      source: unmatched.source,
      name: unmatched.name,
      typeIdByFloorPlanId: unmatched.typeIdByFloorPlanId,
      unit: unmatched.unit,
      countedTotal,
      planValues,
      diagramsValue: unmatched.diagramsValue,
    });
  }

  return { planColumns, rows };
};
